import React from 'react';
import { Modal } from 'react-bootstrap';
import styled from 'styled-components';
import { useLocation, useParams } from 'react-router-dom';

import { Loader } from '../utils/Loader';
import { useEditOutlets } from './hooks/use-edit-outlets.hook';
import BreadCrumbs from '../utils/BreadCrumbs';
import { AddOutletsModal } from './modals/add-outlets.modal';
import { AddVendorsModal } from './modals/add-vendors.modal';

interface AvailableOutlet {
    outlet: string;
    vendorCount: number;
}

interface Outlet {
    productGroupId: number;
    branchCodeId: number;
    outlet: string;
    vendorId: string;
    vendorNickname: string;
    __added?: boolean;
    __removed?: boolean;
}

type Outlets = Record<string, Outlet[]>;

export function EditOutletsPage() {
    const params = useParams();
    const location = useLocation();
    const { groupName } = params;
    const {
        isLoading,
        outlets,
        originalOutlets,
        group,
        handleRemoveVendor,
        handleParkChanges,
        hasChanges,
        isParking,
        handleAddOutlets,
        handleAddVendors,
        isAddOutletsModalOpen,
        isAddVendorsModalOpen,
        availableOutlets,
        availableVendors,
        handleCloseAddOutletsModal,
        handleCloseAddVendorsModal,
        handleSelectOutlet,
        handleConfirmVendors,
    } = useEditOutlets(groupName, location?.state?.group);

    const [isConfirmModalOpen, setIsConfirmModalOpen] = React.useState(false);

    const typedAvailableOutlets = availableOutlets as AvailableOutlet[];
    const typedOutlets = outlets as Outlets;

    const displayOutlets: Outlets = React.useMemo(() => {
        const result: Outlets = {} as Outlets;
        const allOutletNames = Array.from(
            new Set([...Object.keys(originalOutlets || {}), ...Object.keys(typedOutlets || {})])
        );
        allOutletNames.forEach((outletName) => {
            const originalList = (originalOutlets?.[outletName] || []) as Outlet[];
            const currentList = (typedOutlets?.[outletName] || []) as Outlet[];
            const byVendorId: Record<string, Outlet & { __added?: boolean; __removed?: boolean }> = {};
            originalList.forEach((v) => {
                byVendorId[v.vendorId] = { ...v };
            });
            currentList.forEach((v) => {
                if (!byVendorId[v.vendorId]) {
                    byVendorId[v.vendorId] = { ...v, __added: true };
                } else {
                    byVendorId[v.vendorId] = { ...v };
                }
            });
            Object.keys(byVendorId).forEach((vid) => {
                const inOrig = originalList.some((x) => x.vendorId === vid);
                const inCurr = currentList.some((x) => x.vendorId === vid);
                if (inOrig && !inCurr) {
                    byVendorId[vid].__removed = true;
                }
            });
            result[outletName] = Object.values(byVendorId).sort((a, b) =>
                (a.vendorNickname || a.vendorId).localeCompare(b.vendorNickname || b.vendorId)
            );
        });
        return result;
    }, [originalOutlets, typedOutlets]);

    if (isLoading) {
        return <Loader />;
    }

    return (
        <>
            {isAddOutletsModalOpen && (
                <AddOutletsModal
                    availableOutlets={typedAvailableOutlets}
                    onClose={handleCloseAddOutletsModal}
                    onSelectOutlet={handleSelectOutlet}
                />
            )}
            {isAddVendorsModalOpen && (
                <AddVendorsModal
                    availableVendors={availableVendors}
                    onClose={handleCloseAddVendorsModal}
                    onConfirm={handleConfirmVendors}
                    outletName={String(isAddVendorsModalOpen)}
                />
            )}
            <Container>
                <BreadCrumbs path={`/products/update-vendors/${groupName}`} />
                <div className="page-header">
                    <h3>Update Vendors - {group?.label}</h3>
                    <div className="actions">
                        <button
                            className="wfp-btn wfp-btn--secondary"
                            onClick={() => window.history.back()}
                            type="button"
                        >
                            Cancel
                        </button>
                        <button
                            className="wfp-btn wfp-btn--primary"
                            disabled={!hasChanges || isParking}
                            onClick={() => setIsConfirmModalOpen(true)}
                            type="button"
                        >
                            {isParking ? 'Parking Changes...' : 'Park Changes'}
                        </button>
                    </div>
                </div>

                {Object.keys(typedOutlets).length === 0 ? (
                    <EmptyState>
                        <p>
                            <strong>No outlets available for this group</strong>
                        </p>
                    </EmptyState>
                ) : (
                    <OutletsContainer>
                        <div className="outlets-header">
                            <div className="header-content">
                                <div>
                                    <h4>Available Outlets</h4>
                                    <p className="help-text">
                                        Remove outlets from this product group. Each outlet represents a branch
                                        location.
                                    </p>
                                </div>
                                <button
                                    className="wfp-btn wfp-btn--success add-outlets-btn"
                                    onClick={handleAddOutlets}
                                    type="button"
                                >
                                    Add Outlets
                                </button>
                            </div>
                        </div>

                        <OutletsList>
                            {Object.entries(displayOutlets).map(([branchName, branchOutlets]) => (
                                <OutletCard key={branchName}>
                                    <OutletHeader>
                                        <div className="outlet-info">
                                            <h5>{branchName}</h5>
                                            <span className="vendor-count">{branchOutlets.length} vendor(s)</span>
                                        </div>
                                        <button
                                            className="wfp-btn wfp-btn--success"
                                            onClick={() => handleAddVendors(branchName)}
                                            type="button"
                                        >
                                            Add Vendors
                                        </button>
                                    </OutletHeader>

                                    <VendorsList>
                                        {branchOutlets.map((outlet, index) => (
                                            <VendorItem
                                                className={outlet.__added ? 'added' : outlet.__removed ? 'removed' : ''}
                                                key={`${outlet.outlet}-${outlet.vendorNickname}-${index}`}
                                            >
                                                <div className="vendor-info">
                                                    <span className="vendor-name">{outlet.vendorNickname}</span>
                                                    <span className="vendor-id">ID: {outlet.vendorId}</span>
                                                </div>
                                                <div className="vendor-actions">
                                                    {!outlet.__removed && (
                                                        <button
                                                            className="wfp-btn wfp-btn--secondary"
                                                            onClick={() =>
                                                                handleRemoveVendor(branchName, outlet.vendorId)
                                                            }
                                                            type="button"
                                                        >
                                                            Remove
                                                        </button>
                                                    )}
                                                </div>
                                            </VendorItem>
                                        ))}
                                    </VendorsList>
                                </OutletCard>
                            ))}
                        </OutletsList>
                    </OutletsContainer>
                )}
            </Container>
            {isConfirmModalOpen && (
                <ConfirmOutletsUpdateModal
                    current={typedOutlets}
                    groupTitle={group?.label}
                    onClose={() => setIsConfirmModalOpen(false)}
                    onConfirm={async () => {
                        await handleParkChanges();
                        setIsConfirmModalOpen(false);
                    }}
                    original={originalOutlets as Outlets}
                />
            )}
        </>
    );
}

const Container = styled.div`
    max-width: 1200px;
    margin: 0 auto;
    padding: 2rem;

    .page-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 2rem;
        padding-bottom: 1rem;
        border-bottom: 1px solid #e5e5e5;

        h3 {
            margin: 0;
            color: #333;
            font-size: 1.5rem;
        }

        .actions {
            display: flex;
            gap: 1rem;
        }

        .wfp-btn {
            padding: 0.75rem 1.5rem;
            border-radius: 4px;
            border: none;
            cursor: pointer;
            font-size: 0.875rem;
            transition: background-color 0.2s;

            &.wfp-btn--secondary {
                background: #6c757d;
                color: white;

                &:hover {
                    background: #5a6268;
                }
            }

            &.wfp-btn--primary {
                background: #007bff;
                color: white;

                &:hover:not(:disabled) {
                    background: #0056b3;
                }

                &:disabled {
                    background: #6c757d;
                    cursor: not-allowed;
                    opacity: 0.6;
                }
            }
        }
    }
`;

const EmptyState = styled.div`
    text-align: center;
    padding: 4rem 2rem;
    color: #666;
    background: #f8f9fa;
    border-radius: 8px;
    margin-top: 2rem;
`;

const OutletsContainer = styled.div`
    .outlets-header {
        margin-bottom: 2rem;

        .header-content {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            gap: 2rem;
        }

        h4 {
            margin: 0 0 0.5rem 0;
            color: #333;
            font-size: 1.25rem;
        }

        .help-text {
            margin: 0;
            color: #666;
            font-size: 0.9rem;
        }

        .add-outlets-btn {
            background: #28a745;
            color: white;
            padding: 0.75rem 1.5rem;
            border-radius: 4px;
            border: none;
            cursor: pointer;
            font-size: 0.875rem;
            transition: background-color 0.2s;
            white-space: nowrap;

            &:hover {
                background: #218838;
            }

            &:focus {
                outline: 2px solid #28a745;
                outline-offset: 2px;
            }
        }
    }
`;

const OutletsList = styled.div`
    display: grid;
    gap: 1.5rem;
`;

const OutletCard = styled.div`
    border: 1px solid #e5e5e5;
    border-radius: 8px;
    overflow: hidden;
    background: white;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const OutletHeader = styled.div`
    background: rgba(238, 246, 255, 1);
    padding: 1.5rem;
    display: flex;
    justify-content: space-between;
    align-items: center;

    .outlet-info {
        h5 {
            margin: 0 0 0.25rem 0;
            color: #333;
            font-size: 1.1rem;
            font-weight: 600;
        }

        .vendor-count {
            color: #666;
            font-size: 0.9rem;
        }
    }
`;

const VendorsList = styled.div`
    padding: 1rem 1.5rem;
`;

const VendorItem = styled.div`
    display: flex;
    align-items: center;
    padding: 0.75rem 0;
    border-bottom: 1px solid #f0f0f0;

    &:last-child {
        border-bottom: none;
    }

    &.added {
        background: rgba(46, 125, 50, 0.08);
    }

    &.removed {
        opacity: 0.5;
    }

    .vendor-info {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;

        .vendor-name {
            font-weight: 500;
            color: #333;
        }

        .vendor-id {
            font-size: 0.85rem;
            color: #666;
        }
    }

    .vendor-actions {
        margin-left: auto;

        .wfp-btn {
            padding: 0.4rem 0.9rem;
            border: none;
            border-radius: 4px;
            background: #6c757d;
            color: #fff;
            cursor: pointer;
            font-size: 0.8rem;
            transition: background-color 0.2s;
        }

        .wfp-btn:hover {
            background: #5a6268;
        }
    }
`;

// Outlets type is already declared above in this file

function ConfirmOutletsUpdateModal({
    original,
    current,
    groupTitle,
    onClose,
    onConfirm,
}: {
    original: Outlets;
    current: Outlets;
    groupTitle?: string;
    onClose: () => void;
    onConfirm: () => void;
}) {
    const combined = React.useMemo(() => {
        return Object.values(current || {})
            .flat()
            .concat(Object.values(original || {}).flat());
    }, [original, current]);

    const idToOutlet = React.useMemo(() => {
        const map: Record<number, string> = {};
        combined.forEach((v) => (map[v.branchCodeId] = v.outlet));
        return map;
    }, [combined]);

    const idToVendor = React.useMemo(() => {
        const map: Record<number, string> = {};
        combined.forEach((v) => (map[v.branchCodeId] = v.vendorNickname || v.vendorId));
        return map;
    }, [combined]);

    const diff = React.useMemo(() => {
        const originalIds = new Set<number>(
            Object.values(original || {})
                .flat()
                .map((v) => v.branchCodeId as number)
        );
        const currentIds = new Set<number>(
            Object.values(current || {})
                .flat()
                .map((v) => v.branchCodeId as number)
        );
        const removed = Array.from(originalIds).filter((id) => !currentIds.has(id));
        const added = Array.from(currentIds).filter((id) => !originalIds.has(id));

        const grouped: Record<string, { removed: number[]; added: number[] }> = {};
        const ensure = (outlet: string) => (grouped[outlet] ||= { removed: [], added: [] });
        removed.forEach((id) => ensure(idToOutlet[id] || '').removed.push(id));
        added.forEach((id) => ensure(idToOutlet[id] || '').added.push(id));
        return grouped;
    }, [original, current, idToOutlet]);

    return (
        <Modal backdrop="static" centered keyboard={false} onHide={onClose} scrollable show>
            <Modal.Header>
                <Modal.Title>Confirm outlet changes{groupTitle ? ` for ${groupTitle}` : ''}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div>
                    {Object.keys(diff)
                        .sort()
                        .map((outlet) => {
                            const { removed, added } = diff[outlet];
                            return (
                                <div key={outlet} style={{ marginBottom: 12 }}>
                                    <div style={{ fontWeight: 600 }}>{outlet || 'Unknown outlet'}</div>
                                    {removed
                                        .sort((a, b) => (idToVendor[a] || '').localeCompare(idToVendor[b] || ''))
                                        .map((id) => (
                                            <div
                                                key={`r-${outlet}-${id}`}
                                                style={{
                                                    color: 'var(--red, #c62828)',
                                                    textDecoration: 'line-through',
                                                }}
                                            >
                                                {idToVendor[id] || id}
                                            </div>
                                        ))}
                                    {added
                                        .sort((a, b) => (idToVendor[a] || '').localeCompare(idToVendor[b] || ''))
                                        .map((id) => (
                                            <div key={`g-${outlet}-${id}`} style={{ color: 'var(--green, #2e7d32)' }}>
                                                {idToVendor[id] || id}
                                            </div>
                                        ))}
                                </div>
                            );
                        })}
                </div>
            </Modal.Body>
            <Modal.Footer>
                <button className="wfp-btn" onClick={onClose} type="button">
                    Cancel
                </button>
                <button className="wfp-btn--primary" onClick={onConfirm} type="button">
                    Confirm
                </button>
            </Modal.Footer>
        </Modal>
    );
}
